import {
  Major,
  CareerPath,
  NUGGET_VARIANTS,
  getCollegeIcon,
  majorTransform,
} from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { handleShareWithNavigator } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { ResourceHeader } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceHeader';
import { ResourceWrapper } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceWrapper';
import { ResourcePageSection } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/types';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { AcademicExperienceSection } from './molecules/AcademicExperienceSection';
import { CareerOutcomesSection } from './molecules/CareerOutcomesSection';
import { EarningsSection } from './molecules/EarningsSection';
import { GraduateSchoolSection } from './molecules/GraduateSchoolSection';
import { MajorOverview } from './molecules/MajorOverview';

const STRINGS = appStrings.modules.majors;

type MajorDetailContentProps = Readonly<{
  major: Major;
  careerPaths: CareerPath[];
}>;

export function MajorDetailContent({ major, careerPaths }: MajorDetailContentProps) {
  const dispatch = useAppDispatch();
  const { data: bookmarks } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();

  const bookmarkId = getBookmarkIdForNugget(bookmarks, major?.id, 'major');
  const GROW_URL = process.env.REACT_APP_GROW_URL;

  const handleBookmark = async () => {
    if (major?.id) {
      if (bookmarkId) {
        await deleteBookmark(bookmarkId);
      } else {
        await postBookmark({
          type: 'major',
          itemId: major.id,
        });
      }
    }
  };

  const handleShare = () => {
    handleShareWithNavigator({
      title: major.name,
      text: major.description || '',
      url: `${GROW_URL}/majors/${major.id}`,
    });
  };

  const handleCreateTask = () => {
    dispatch(
      setNuggetToAdd({
        id: major.id,
        slug: major.name || '',
        title: major.name,
        description: major.description || '',
        star_rating: 0,
        value_rating: 0,
        is_browsable: false,
        variant: NUGGET_VARIANTS.MAJOR,
        major_info: major,
      }),
    );
    dispatch(openPlaylistListStep());
  };

  const PAGE_SECTIONS: ResourcePageSection[] = [
    {
      id: 'description',
      title: 'Description',
      hideFromMenu: true,
      hideHeader: true,
      component: <MajorOverview major={major} careerPaths={careerPaths} />,
    },
    {
      id: 'academic-experience',
      title: 'Academic Experience',
      description: STRINGS.academicExperience.sectionDescription,
      headerRightComponents: [
        <LubavButton
          key="major-academic-lubav-btn"
          id="major-academic-lubav-btn"
          label="Compare to other majors"
          prompt={`Compare the academic experience attributes of ${major.name} to other similar majors. How does it stack up in terms of demand, rigor, specialization, competitiveness, and workload?`}
          useGeneralKnowledge
        />,
      ],
      component: <AcademicExperienceSection major={major} />,
    },
    {
      id: 'career-outcomes',
      title: 'Career Outcomes',
      description: STRINGS.careerOutcomes.sectionDescription,
      headerRightComponents: [
        <LubavButton
          key="major-career-outcomes-lubav-btn"
          id="major-career-outcomes-lubav-btn"
          label="Explain this to me"
          prompt={`Explain the career outcomes for ${major.name}. What does the underemployment rate mean, and what career paths and sample roles are available?`}
          allowTools={false}
        />,
      ],
      component: <CareerOutcomesSection major={major} careerPaths={careerPaths} />,
    },
    ...(major.medianWageEarlyCareer && major.medianWageMidCareer
      ? [
          {
            id: 'earnings',
            title: 'Earnings',
            description: STRINGS.earnings.sectionDescription,
            headerRightComponents: [
              <LubavButton
                key="major-earnings-lubav-btn"
                id="major-earnings-lubav-btn"
                label="Summarize this for me"
                prompt={`Summarize the earnings data for ${major.name}. How do the early and mid-career wages compare to the average across all majors?`}
                allowTools={false}
              />,
            ],
            component: <EarningsSection major={major} />,
          },
        ]
      : []),
    {
      id: 'graduate-school',
      title: 'Graduate School',
      description: STRINGS.graduateSchool.sectionDescription,
      headerRightComponents: [
        <LubavButton
          key="major-grad-school-lubav-btn"
          id="major-grad-school-lubav-btn"
          label="Popular graduate programs"
          prompt={`What are the most popular and well-regarded graduate programs for someone with a ${major.name} degree? What types of graduate degrees do ${major.name} graduates typically pursue?`}
          allowTools={false}
          useGeneralKnowledge
        />,
      ],
      component: <GraduateSchoolSection major={major} />,
    },
  ];

  return (
    <ResourceWrapper
      header={
        <ResourceHeader
          title={major.name}
          subtitle={major.category}
          banner_media_url={major.media_url}
          nugget={majorTransform(major)}
          titleIcon={{
            icon: getCollegeIcon(major.typicalCollege),
            backgroundColor: 'Error/400',
          }}
          headerActions={
            <LubavButton
              id="major-good-fit-lubav-btn"
              label="Is this major a good fit for me?"
              prompt={`Is ${major.name} a good fit for me? Consider my interests, goals, and background to help me decide.`}
              allowTools={false}
              useGeneralKnowledge
            />
          }
          buttonGroup={[
            {
              onClick: handleBookmark,
              icon: bookmarkId ? 'BookmarkCheck' : 'Bookmark',
              tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
            },
            {
              onClick: handleShare,
              icon: 'Share06',
              tooltipOnClickText: 'Copied!',
              tooltipHoverText: commonAppStrings.modules.actionTabs.share,
            },
            {
              onClick: handleCreateTask,
              icon: 'ClipboardPlus',
              tooltipHoverText: commonAppStrings.modules.actionTabs.task,
            },
          ]}
        />
      }
      sections={PAGE_SECTIONS}
      showTabs={false}
    />
  );
}
